import GenericApiAdapter from '../adapters/genericApiAdapter.js';
import RetryPaymentApiAdapter from '../adapters/retryApiAdapter.js';
import { getDefaultConfig } from '../utils/configUtils.js';

/* We call "retryPaymentBaseUrl" as "paymentBffBaseUrl" because of PayPal Autoshipment
 * implementation.
 * In this implementation, a dedicated BFF is needed for payment-frontend.
 *
 * We decided to reuse the "retry-payment" as "payment-bff", as mentioned in this wiki:
 * https://wiki.web.zooplus.de/x/TwFhF
 *
 * There should be another task to rename "retry-payment" to something like "payment-bff".
 * Also a ticket is created to rename "retryPaymentBaseUrl" to "paymentBffBaseUrl" after
 * the BFF itself has been renamed: https://tracker.zooplus.de/browse/PAYM-515 */
const { retryPaymentBaseUrl: paymentBffBaseUrl } = getDefaultConfig();
/**
 * Service to operate PayPal related stuff and API calls.
 */
class PayPalPaymentService {
    static genericApi;
    static paymentBffApi;
    static initiated = false;
    /**
     * Initiate the service. Since the API adapter used in this class depends on
     * runtime values (domain, locale,...), it needs to be initiated before using.
     *
     * The initiate call might be placed at the top level of the component tree,
     * so that the service will be available for the calls make from the child components.
     *
     * @param domain The running shop domain. This value will be used as the "X-Original-Host"
     *               header before sending the request to BFF.
     * @param locale The running shop locale. This value will be used as the "Accept-Language"
     *               header before sending the request to BFF.
     */
    static init(domain, locale) {
        if (this.initiated) {
            return;
        }
        this.genericApi = new GenericApiAdapter('');
        this.paymentBffApi = new RetryPaymentApiAdapter(paymentBffBaseUrl, domain, locale);
        this.initiated = true;
    }
    /**
     * Get the generated userIdToken to load the PayPal Javascript SDK for a given site.
     * This userIdToken will be used as "data-user-id-token" in the script tag of the SDK.
     *
     * @param siteId The site ID to get userIdToken for.
     */
    static async getUserIdToken(siteId) {
        this.makingSureInitiated();
        const response = await this.paymentBffApi.get(`/id-token/${siteId}`);
        return response.id_token;
    }
    /**
     * Create a PalPal order by calling checkout API.
     */
    static async createOrder(amountMajorUnit) {
        this.makingSureInitiated();
        const data = await this.genericApi.get('/checkout/paypal-order', {}, {
            previewPageAmount: amountMajorUnit,
        });
        return data.paypalOrderId;
    }
    /**
     * Create PayPal vault setup token, this token will be used in the Purchase Later flow to
     * save PayPal payment method.
     */
    static async createVaultSetupToken(siteId) {
        this.makingSureInitiated();
        const data = await this.paymentBffApi.get(`/setup-token/${siteId}`);
        return data.setupTokenId;
    }
    /**
     * Create a payment token from an approved "vault setup token".
     *
     * @param vaultSetupToken The approved vault setup token.
     * @param customerId The customerId
     * @param siteId The siteId
     */
    static async createPaymentToken(vaultSetupToken, customerId, siteId) {
        this.makingSureInitiated();
        const body = { tokenId: vaultSetupToken, customerId, siteId };
        return await this.paymentBffApi.post('/setup-token/payment-token', body);
    }
    /**
     * Making sure that this Service is initiated before one of its function is used. Otherwise,
     * the API adapter will not be available for making REST calls.
     *
     * This function will throw an error if the Service is not yet initiate.
     */
    static makingSureInitiated() {
        if (!this.initiated) {
            throw new Error('PayPalPaymentService is used before it is initiated');
        }
    }
}

export { PayPalPaymentService as default };
//# sourceMappingURL=PayPalPaymentService.js.map
